import TextLink from "~/app/_components/common/textLink";
import { INCLUSO_PHONE } from "./value";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { SERVICE_LIST } from "./services";
import {
  ContactButton,
  type ContactButtonProps,
} from "~/app/_components/pageSpecific/contact/contactButton";
import { ContactUs, Services } from "./navLinks";

export const InclusoPhoneLink = (
  <TextLink
    tel
    href={INCLUSO_PHONE}
    text={INCLUSO_PHONE}
    className="mx-1 font-bold"
  />
);

export const ContactBookLink = ({ children, icon }: ContactButtonProps) => {
  return (
    <ContactButton link section="book" className="font-bold">
      {icon ?? <ArrowRightIcon className="mb-0.5 ml-1 inline size-5" />}
      {children ?? "Book a free consultation today."}
    </ContactButton>
  );
};

export const FAQ_PERSONAL_DATA = [
  {
    question: "What is Personal and In-home Care Support?",
    answer:
      "Personal and In-home Care Support involves assistance provided within an individual's home to help with daily activities and ensure a comfortable and safe living environment. This can include personal care, household tasks, and other support services tailored to individual needs.",
  },
  {
    question:
      "What services are typically included in Personal and In-home Care Support? ",
    answer: (
      <>
        <p>Services may include:</p>
        <ul className="ml-[1rem] list-disc">
          {SERVICE_LIST.personal.service.map((item, i) => (
            <li key={i}>
              <strong>{item.name}: </strong>
              <span>{item.description}</span>
            </li>
          ))}
        </ul>
        <p>
          Chat with our team at
          {InclusoPhoneLink}
          to discuss tailored service for you or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "How do I access Personal and In-home Care Support?",
    answer:
      "To access these services, you need to have an NDIS plan that includes funding for personal and in-home care. You can discuss your specific needs and goals with your NDIS planner or Local Area Coordinator (LAC) to ensure the appropriate supports are included in your plan.",
  },
  {
    question: "Who can benefit from Personal and In-home Care Support? ",
    answer:
      "Individuals who have an NDIS plan and need help managing daily living activities or maintaining their home environment can benefit from these services. This includes people with various disabilities or conditions that impact their ability to perform everyday tasks.",
  },
  {
    question: "How often will I receive Personal and In-home Care Support? ",
    answer: (
      <>
        <p>
          The frequency and duration of support are determined by your
          individual needs and the details outlined in your NDIS plan. This can
          be arranged with your support provider to fit your schedule and
          preferences. Chat with our team at
          {InclusoPhoneLink}
          to discuss your preferred arrangements or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "What should I do if my needs change? ",
    answer: (
      <p>
        If your needs change, contact your NDIS planner or LAC to discuss
        updating your plan. This may involve adjusting your current support
        services or adding new ones to better meet your evolving needs. Chat
        with our team at
        <TextLink
          tel
          href={INCLUSO_PHONE}
          text={INCLUSO_PHONE}
          className="mx-1 font-bold"
        />
        to adjust your support arrangements.
      </p>
    ),
  },
  {
    question:
      "Are there any additional costs associated with Personal and In-home Care Support? ",
    answer: (
      <>
        <p>
          Typically, NDIS funding covers the costs of personal and in-home care
          support as specified in your plan. However, it’s important to clarify
          any potential additional costs with your support provider to avoid
          unexpected expenses.
          <ContactBookLink>
            Book a free consultation today to organise a quote.
          </ContactBookLink>
        </p>
      </>
    ),
  },
  {
    question:
      "How is NDIS funding used for Personal and In-home Care Support? ",
    answer:
      "NDIS funding for Personal and In-home Care Support is used to cover the costs associated with providing care and assistance within your home. This funding is part of your NDIS plan and is allocated based on your individual needs and goals.",
  },
];

export const FAQ_SUPPORT_DATA = [
  {
    question: "What is a Support Coordinator?",
    answer:
      "A Support Coordinator is a professional who helps NDIS participants understand and implement their NDIS plan. They assist with coordinating and managing supports, connecting with service providers, and ensuring you get the most out of your funding.",
  },
  {
    question: "What does a Support Coordinator do?",
    answer: (
      <>
        <p>Support Coordinators help you:</p>
        <ul className="ml-[1rem] list-disc">
          <li>Understand and utilise your NDIS plan and funding</li>
          <li>
            Connect with service providers and manage informal, mainstream, and
            funded supports
          </li>
          <li>
            Develop strategies to achieve your goals and increase your
            independence
          </li>
          <li>Navigate and resolve any issues or challenges that arise</li>
        </ul>
        <p>
          Chat with our team at
          {InclusoPhoneLink}
          to discuss your level of support or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "How can a Support Coordinator help me?",
    answer: (
      <>
        <p>A Support Coordinator can help you by:</p>
        <ul className="ml-[1rem] list-disc">
          <li>Guiding you in planning and budgeting for your NDIS supports</li>
          <li>Coordinating and managing services from different providers</li>
          <li>Assisting with accessing community and mainstream services</li>
          <li>
            Helping you make informed decisions and resolve any issues related
            to your plan
          </li>
        </ul>
        <ContactBookLink icon={false}>
          Book a free consultation today to discover how we can help.
        </ContactBookLink>
      </>
    ),
  },
  {
    question: "Are Support Coordinators covered by NDIS funding?",
    answer:
      "Yes, Support Coordination is an NDIS-funded service. The cost of a Support Coordinator's services is covered under the “Support Coordination” category in your NDIS plan. The availability of this funding depends on your specific plan and needs.",
  },
  {
    question: "Can I choose my Support Coordinator?",
    answer: (
      <>
        <p>
          Yes, most definitely can.
          <TextLink href={ContactUs.href} className="mx-1 font-bold">
            Contact us
          </TextLink>
          to preview our staff catalogue and you can request when you place your
          booking.
        </p>
      </>
    ),
  },
  {
    question: "How often will I meet with my Support Coordinator?",
    answer: (
      <>
        <p>
          The frequency of meetings with your Support Coordinator depends on
          your individual needs and goals. Some participants may meet regularly,
          while others may require less frequent contact. This can be discussed
          and agreed upon with your Support Coordinator. Chat with our team at
          {InclusoPhoneLink}
          to discuss or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "Can I change my Support Coordinator if I'm not satisfied?",
    answer:
      "Yes, you have the right to request a change if you are not satisfied with your current Support Coordinator. You can discuss your concerns with them, and if necessary, contact your NDIS planner or Local Area Coordinator to arrange a change.",
  },
  {
    question: "How do I know if Support Coordination is right for me?",
    answer:
      "Support Coordination is beneficial if you need assistance managing and coordinating multiple supports, understanding your NDIS plan, or achieving your goals. If you find it challenging to navigate the system or need help connecting with services, a Support Coordinator can provide valuable support.",
  },
];

export const FAQ_COMMUNITY_DATA = [
  {
    question: "What is Community Engagement?",
    answer:
      "Community Engagement or community participation activities in NDIS services are essential for empowering participants to actively connect with their communities through a range of activities and programs. These services are designed to foster social interaction, promote skill development, and enhance overall well-being. By offering meaningful opportunities for connection, Community Participation providers help individuals build stronger ties within their local communities and lead more fulfilling lives.",
  },
  {
    question:
      "How do I access community participation funding under the NDIS? ",
    answer:
      "You can request funding for community participation during your NDIS planning meeting. The NDIS planner will assess your needs and may allocate funds for community-based supports. ",
  },
  {
    question: "Can I choose my community participation provider?  ",
    answer: (
      <>
        <p>
          Yes, participants have the flexibility to choose their community
          participation provider based on their goals and preferences. Chat with
          our team at
          {InclusoPhoneLink}
          to discuss your interests in Community Engagement or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question:
      "Can family members participate in community engagement activities?",
    answer:
      "While community engagement activities are primarily for NDIS participants, family members may sometimes be involved, particularly in social or recreational activities designed to build family bonds.",
  },
  {
    question:
      "What kind of support does Incluso Services provide for community engagement?",
    answer: (
      <>
        <p>
          We offer assistance with registering for activities, connecting with
          social groups, organising outings, and accessing community-based
          events and programs to suit your interests. Chat with our team at
          {InclusoPhoneLink}
          to discuss your needs or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question:
      "Are transportation costs included in community participation funding?",
    answer:
      "Transportation support may be covered under your NDIS plan. Check with your NDIS planner or LAC to ensure transportation is included.",
  },
  {
    question: "How do I get started with Community Engagement Services? ",
    answer: (
      <>
        <p>
          <TextLink href={ContactUs.href}>Contact Incluso Services </TextLink>
          or your NDIS planner to discuss your goals and find the best
          activities and support to suit your needs.{" "}
          <ContactBookLink icon={false}>
            Book a free consultation with our team today.
          </ContactBookLink>
        </p>
      </>
    ),
  },
];

export const FAQ_ACCOMMODATION_DATA = [
  {
    question: "What is Supported Independent Living (SIL)?",
    answer:
      "SIL is a type of NDIS support that helps individuals with high support needs live independently, either on their own or in shared accommodation. It provides 24/7 assistance with daily tasks like personal care, cooking, and mobility.",
  },
  {
    question: "Who is eligible for SIL?",
    answer:
      "SIL is available to NDIS participants who have high support needs and require significant daily assistance. Eligibility is determined through your NDIS plan and the level of support required.",
  },
  {
    question: "What types of accommodation are available with SIL?",
    answer:
      "Accommodation options vary and can include shared living arrangements, individual units, or group homes. The focus is on finding a living arrangement that meets your needs and preferences.",
  },
  {
    question: "Can I choose who I live with in a SIL arrangement?",
    answer: (
      <>
        <p>
          Yes, you can often have input into choosing housemates in a shared
          accommodation setup, ensuring compatibility and a positive living
          environment. Chat with our team at
          {InclusoPhoneLink}
          to discuss your arrangement needs or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question:
      "How does SIL differ from SDA (Specialist Disability Accommodation)?",
    answer:
      "Yes, SIL can help with transitioning from hospital, aged care, or other types of care environments to a more independent living arrangement.",
  },
  {
    question:
      "Can I transition to SIL from other forms of care (e.g., hospital or aged care)?",
    answer:
      "To apply for SIL, you must discuss your need for independent living support with your NDIS planner or Local Area Coordinator (LAC). They will assess your situation and determine if SIL is appropriate for your NDIS plan.",
  },
  {
    question: "How is the level of support in SIL determined?",
    answer: (
      <>
        <p>
          The level of support is based on your individual needs, as assessed by
          your NDIS planner. This can range from shared support to one-on-one
          assistance, depending on your daily living requirements. Chat with our
          team at
          {InclusoPhoneLink}
          to discuss your needs or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "What are the benefits of SIL? ",
    answer:
      "SIL promotes independence while providing necessary support. It helps participants build skills, increase community involvement, and improve their quality of life by living as autonomously as possible.",
  },
];

export const FAQ_TRANSPORT_DATA = [
  {
    question: "What is transport support?",
    answer:
      "Transport support is assistance provided to help individuals who have difficulty accessing transportation due to physical, cognitive, or financial limitations, allowing them to travel for work, medical appointments, social activities, and other essential trips.",
  },
  {
    question: "Who is eligible for transport support?",
    answer:
      "Transport funding is available for NDIS participants who cannot use public transport due to their disability. This includes individuals who are unable to drive or travel independently and need support to access their community.",
  },
  {
    question: "How do I apply for transport funding?",
    answer:
      "You can discuss your transport needs during your NDIS planning meeting or review. Your planner will assess your situation and determine if transport funding is appropriate for your plan.",
  },
  {
    question: "Can I use transport funding for family or carers to drive me?",
    answer:
      "Generally, NDIS transport funding is not meant to reimburse family or informal supports for providing transport. It’s intended for professional transport services or support workers.",
  },
  {
    question: "Can I use my NDIS funding for public transport?",
    answer:
      "Yes, in some cases, transport support can be used to cover the cost of public transport fares if you are unable to travel independently and need assistance to do so",
  },
  {
    question: "Can I combine transport funding with other NDIS supports?",
    answer: (
      <>
        <p>
          Yes, transport support can work alongside other supports like
          assistance with daily living or community participation to ensure you
          get to appointments, social activities, or work. Chat with our team at
          {InclusoPhoneLink}
          to discuss your comprehensive needs or
          <ContactBookLink />
        </p>
      </>
    ),
  },
  {
    question: "Can transport funding be used for vehicle modifications?",
    answer:
      "No, vehicle modifications are covered under a different section of the NDIS. Transport funding is specifically for travel costs and services, not for modifying vehicles.",
  },
];

export const FAQ_INSIGHTS_DATA = [
  {
    question: "How is Incluso different to other disability support providers?",
    answer: (
      <>
        <p>
          Incluso is challenging the status quo, moving away from the tiresome,
          traditional paper-based methods and leveraging technology for its
          ability to improve efficiencies, accuracy, and education. We’re here
          to help make everything a lot simpler.
        </p>
        <p>We do this by offering:</p>
        <ul className="ml-[1rem] list-disc">
          <li>
            NDIS application process guidance for all individuals, to help to
            optimise your NDIS Plan.
          </li>
          <li>One point of contact for everyday tasks and feedback.</li>
          <li>
            Flexible support options through our fee-for-service structure.
          </li>
          <li>Guaranteed compliance with the NDIS Code of Conduct.</li>
          <li>
            An affective complaint management process should the need occur.
          </li>
        </ul>
        <p>
          <TextLink href={ContactUs.href} className="mr-1.5 font-semibold">
            Reach out
          </TextLink>
          for a chat.
        </p>
      </>
    ),
  },
  {
    question: "How much will I pay for your services?",
    answer: (
      <>
        <p>
          Our fee-for-service structure allows us to offer flexible support
          options. Visit our{" "}
          <TextLink href={Services.href} className="font-semibold">
            price guide
          </TextLink>{" "}
          or{" "}
          <TextLink href={ContactUs.href} className="mr-1.5 font-semibold">
            Contact us
          </TextLink>
          to find out more.
        </p>
      </>
    ),
  },
  {
    question: "Do I have choice over who my support worker is?",
    answer: (
      <>
        <p>
          This is your decision, and we help make it happen. At Incluso, we
          ensure that you are happy with your support worker by also ensuring
          they are a great match. If you like going swimming, we will ensure
          it’s a support worker that also loves swimming!
        </p>
        <p>
          To find out more about our wonderful support workers you can{" "}
          <TextLink href={ContactUs.href} className="font-semibold">
            Contact us
          </TextLink>{" "}
          to discuss your preference.
        </p>
      </>
    ),
  },
  {
    question: "How can I contact for your help?",
    answer: (
      <>
        <p>
          You can contact us by phone, email, social media, post, or come and
          visit us in person.{" "}
          <TextLink href={ContactUs.href} className="font-semibold">
            See details
          </TextLink>
        </p>
      </>
    ),
  },
  {
    question: "How does NDIS housing work?",
    answer: (
      <>
        <p>
          NDIS housing, often referred to as "Supported Independent Living"
          (SIL) and "Specialist Disability Accommodation" (SDA), is designed to
          help people with disability find appropriate, supportive, and
          accessible housing. Here’s an overview of how NDIS housing work:
        </p>
        <ul className="ml-[1rem] list-decimal">
          <li>
            <strong>Assessment: </strong>
            The NDIS assesses your needs and preferences for housing.This
            assessment helps determine if you require SIL, SDA or ILO and the
            level of support you need.
          </li>
          <li>
            <strong>Housing Providers: </strong>
            For SIL, you can choose from a range of service providers that offer
            support in various types of accommodation. For SDA, you can select
            from properties that meet your needs and preferences.
          </li>
          <li>
            <strong>Application: </strong>
            If you are eligible for SDA, you may need to go through a process to
            select or apply for SDA housing. This can involve working with
            housing providers or support coordinators to find an appropriate
            property.
          </li>
          <li>
            <strong>Transition: </strong>
            Moving into new housing can involve a transition period where
            support services are arranged, and you adapt to the new living
            environment. The NDIS and housing providers typically assist with
            this process to ensure a smooth transition.
          </li>
        </ul>
        <p>
          Once in an SIL arrangement, you will work with your support provider
          to ensure your needs are met and adjust support as required.
        </p>
        <p>
          To discuss your housing support plan and goals, you can{" "}
          <ContactBookLink icon={false}>
            book a free consultation
          </ContactBookLink>{" "}
          with our team.
        </p>
      </>
    ),
  },
  {
    question: "How do I use my NDIS plan?",
    answer: (
      <>
        <p>
          Using your NDIS plan effectively is crucial to achieving your goals
          and accessing the supports you need. Here’s a step-by-step guide to
          help you get started:
        </p>
        <ul className="ml-[1rem] list-decimal">
          <li>
            <strong>Understand </strong>
            your plan, focus on the core sections: your goals, funded supports,
            and the breakdown of your budget across different support
            categories.
          </li>
          <li>
            <strong>Connect </strong>
            with Your Local Area Coordinator (LAC) or Support Coordinator
          </li>
          <li>
            <strong>Research </strong>
            providers by looking online, through Facebook groups and using the
            NDIS provider finder
          </li>
          <li>
            <strong>Manage </strong>
            your budget by tracking spending
          </li>
          <li>
            <strong>Implement </strong>
            your supports and start services
          </li>
          <li>
            <strong>Start </strong>
            services and begin accessing the services outlined in your plan.
          </li>
          <li>
            <strong>Keep updated, </strong>
            it is important that you keep up-to-date with any changes or updates
            to the NDIS that may affect your plan as it’s a fast changing
            environment.
          </li>
        </ul>
        <p>
          By following these steps, you can effectively use your NDIS plan to
          access the supports you need, work towards your goals, and improve
          your overall well-being.
        </p>
        <p>
          If you require any assistance with the following steps, you can{" "}
          <ContactBookLink icon={false}>
            book a free consultation
          </ContactBookLink>{" "}
          with our team.
        </p>
      </>
    ),
  },
  {
    question: "Once my plan is approved, what’s next?",
    answer: (
      <>
        <p>
          Once the NDIS approve your plan, you can start using it. This means
          you can spend your NDIS funding on the supports set out in your plan
          instantly.
        </p>
        <p>
          The NDIS will send a copy of your plan within 7 days after it is
          approved. You can also find your plan in the “my NDIS” portal and app.
        </p>
        <p>
          Your plan will include information about you, your situation and your
          goals. It will also include supports to help you pursue your goals as
          well as information on funding allocated to each support category.
        </p>
        <p>
          The next step would be finding a Support Coordinator if you have this
          specific funding or looking for Service Providers.
        </p>
        <p>
          The Incluso Services team have a wealth of knowledge when it comes to
          the NDIS process, if you have any questions regarding the process you
          can{" "}
          <ContactBookLink icon={false}>
            book a free consultation
          </ContactBookLink>{" "}
          with our team.
        </p>
      </>
    ),
  },
  {
    question: "What happens when I run out of NDIS funding?",
    answer: (
      <>
        <p>
          When this happens, you will need to cease services otherwise you may
          be personally responsible for the costs you incur.
        </p>
        <p>
          The key is to be aware of your budget and plan well ahead of this
          happening. Your Support Coordinator or LAC should be able to assist
          and guide you through the process of requesting a new plan so that you
          can continue with services.
        </p>
        <p>
          In an emergency you should let the NDIS know of your situation and
          reach out to groups like Carer Gateway to see if you can gain
          emergency funding.
        </p>
        <p>
          If you do find yourself in a situation like this, we are only a call
          away to help you navigate the situation. Call us on{InclusoPhoneLink}
          for further discussion.
        </p>
      </>
    ),
  },
  {
    question: "What is community access and participation?",
    answer: (
      <>
        <p>
          Community Access and Participation within the NDIS refers to the
          support provided to individuals to help them engage and participate in
          their communities. This type of support promotes social inclusion,
          independence, and overall well-being.
        </p>
        <p>
          One of the ways that Incluso Services provides Community Access and
          Participation is collaborating with our local community centres where
          we get involved with community programs and activities.
        </p>
        <p>
          If you would like to learn more about how we provide support within
          the community or to join our Incluso community{" "}
          <TextLink href={ContactUs.href} className="font-semibold">
            contact us
          </TextLink>{" "}
          for a chat.
        </p>
      </>
    ),
  },
];
